import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { initRum } from './observability/rum';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css';

initRum();

const envMode = process.env.REACT_APP_ENV;
const currentPath = window.location.pathname;

const shouldEnableSentry =
    ['prod', 'test', 'dev'].includes(envMode) &&
    !currentPath.startsWith('/recording/room') &&
    !currentPath.startsWith('/feedback/room');

// `beam` is a headless recording peer — it never fires real mouse/keyboard
// DOM events, so Sentry Replay's SDK-internal 5-minute idle-pause (not
// exposed as a config option) treats it as inactive and truncates the
// recording. Dispatching a synthetic keydown through the SDK's own listener
// keeps its last-activity timestamp fresh so the full session gets recorded.
const isBeamSession = currentPath.split('/').includes('beam');

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason) {
    if (!sentryInitialized) return;

    const now = Date.now();

    // Prevent restart spam (5s debounce)
    if (now - lastRestartAt < 5000) return;
    lastRestartAt = now;

    try {
        Sentry.endSession();
        Sentry.startSession();

        Sentry.setContext('session_restart', {
            reason: reason,
            timestamp: new Date().toISOString(),
        });

        Sentry.flush(5000);
    } catch (e) {
        console.warn('[Sentry] Failed to restart session', e);
    }
}

if (shouldEnableSentry) {
    Sentry.init({
        dsn: 'https://01c968ca26cd3e144998246f3c490163@o4506863228616704.ingest.us.sentry.io/4506863241723904',

        release: process.env.REACT_APP_VERSION,
        environment: envMode,

        autoSessionTracking: true,

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [
                    /^https:\/\/event\.100ms\.live\/v2\/client\/report/,
                    /^https:\/\/dev\.api\.incruiter\.com/,
                    /^https:\/\/test\.api\.incruiter\.com/,
                    /^https:\/\/api\.incruiter\.com/,
                    /^https:\/\/.*\.incruiter\.com/,
                ],
                networkRequestHeaders: ['Authorization', 'Content-Type'],
                networkResponseHeaders: ['Content-Type'],
            }),
            Sentry.replayCanvasIntegration(),
        ],

        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

        denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\//],

        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,

        tracePropagationTargets: ['localhost', /^https:\/\/.*\.incruiter\.com/],
        enableLogs: true,

        // Firefox rejects internal WebRTC/media-pipeline promises (that this app
        // never creates) with the plain string "out of memory" instead of an
        // Error when it hits its own internal resource ceiling. It surfaces here
        // as an unhandledrejection with no app stack trace and isn't actionable
        // from application code, so it's filtered as noise rather than reported.
        ignoreErrors: [
            /^(Non-Error|Object captured as) (exception|promise rejection) with keys/,
            'Non-Error promise rejection captured with value: out of memory',
            'Permissions check failed',
            /^Event `.*` \(type=.*\) captured as (promise rejection|exception)/,
        ],
    });

    sentryInitialized = true;

    // 🔁 Restart session when network reconnects
    window.addEventListener('online', function () {
        restartSentrySession('network_online');
    });

    // 🔁 Restart session when tab becomes visible again
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            restartSentrySession('tab_visible');
        }
    });

    // Keep Replay's idle timer alive for the beam peer (see isBeamSession above).
    if (isBeamSession) {
        setInterval(
            () => {
                window.dispatchEvent(new KeyboardEvent('keydown'));
            },
            2 * 60 * 1000
        );
    }
}

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);
